import React, { memo } from 'react';
import { SFCard, SFScrollable } from 'sfui';
import { isEqualObject } from 'ui-smartforce-settings';
import { ReportValue, VersionValueType } from '../../Models';
import Loader from '../Loader/Loader';
import { ContactMap } from './ContactMap/ContactMap';
import styles from './ReportCard.module.scss';
import { ReportCardHeader } from './ReportCardHeader/ReportCardHeader';
import { ReportCardInfo } from './ReportCardInfo/ReportCardInfo';
import {
  ReportHistory,
  ReportHistoryItem
} from './ReportHistory/ReportHistory';
import { ReportHistoryAvatar } from './ReportHistoryAvatar/ReportHistoryAvatar';
import { ReportHistoryPanel } from './ReportHistoryPanel/ReportHistoryPanel';
import { ReportListItem } from '../ReportList/ReportList';
import { ReportableStatus } from './ReportableStatus/ReportableStatus';
import { ReportableStatusModal } from './ReportableStatusModal/ReportableStatusModal';
import { useReportCard } from './Hooks/useReportCard';
import { formatDate } from '../../Helpers';
import { isStateReportable } from './Helpers';

export interface ReportCardProps {
  report: ReportListItem;
  showAmend: boolean;
  showReportable: boolean;
  onScanQrCode: (activeReport: ReportValue) => void;
  onUpdateReportableStatus: (report: ReportValue) => void;
}

export const ReportCard = memo(
  function ({
    report,
    showAmend,
    showReportable,
    onScanQrCode,
    onUpdateReportableStatus
  }: ReportCardProps): React.ReactElement<ReportCardProps> {
    const {
      isHistoryOpen,
      isLoading,
      isLargeScreen,
      customer,
      reportActive,
      selectedItem,
      isAmendDisabled,
      isSaveButtonDisabled,
      showReportableStatusModal,
      reportableStatus,
      onViewHistory,
      onReportHistoryClick,
      onAmend,
      onSeeFullReport,
      onHistoryPanelClose,
      onHistoryPanelBack,
      onHistoryPanelSave,
      onReportableStatusButtonClick,
      onReportableStatusChange,
      onReportableStatusDiscard,
      onReportableStatusSave,
      mapCoords,
      contactSummaryValue,
      cadSummaryValue,
      areasName
    } = useReportCard({
      report,
      showAmend,
      onUpdateReportableStatus
    });

    const getHistoryItems = (
      versions: VersionValueType[]
    ): ReportHistoryItem[] => {
      return versions.map((version: VersionValueType) => ({
        version: version.v,
        title: formatDate(version.created_at, 'L - HH:mm', customer.timezone),
        subtitle:
          version.v === 0
            ? 'Original version'
            : version.v === (report.versions?.length as number) - 1
              ? 'Current version'
              : '',
        chipText: version.from_public_api ? 'Created via API' : undefined,
        children: (
          <ReportHistoryAvatar
            name={version.author.name}
            title={version.author.title?.name}
            avatar={version.author.avatar_thumbnail_url}
          />
        )
      }));
    };

    return (
      <>
        <ReportHistoryPanel
          isOpen={isHistoryOpen}
          isLoading={isLoading}
          isSaveButtonDisabled={isSaveButtonDisabled}
          onClose={onHistoryPanelClose}
          onBack={onHistoryPanelBack()}
          onSave={onHistoryPanelSave()}
        >
          {!showReportableStatusModal && (
            <ReportHistory
              items={getHistoryItems(report.versions as VersionValueType[])}
              isAmendDisabled={isAmendDisabled}
              isReportableDisabled={!showReportable}
              selectedIndex={selectedItem}
              tooltipType={report.tooltipType}
              reportStatus={report.status}
              onItemClick={onReportHistoryClick}
              onAmend={onAmend}
              onReportableStatusButtonClick={onReportableStatusButtonClick}
            />
          )}

          {showReportableStatusModal && (
            <ReportableStatus
              status={reportableStatus}
              onChange={onReportableStatusChange}
            />
          )}
        </ReportHistoryPanel>

        <ReportableStatusModal
          isOpen={showReportableStatusModal && isLargeScreen}
          isLoading={isLoading}
          isSaveButtonDisabled={isSaveButtonDisabled}
          status={reportableStatus}
          onDiscard={onReportableStatusDiscard}
          onChange={onReportableStatusChange}
          onSave={onReportableStatusSave}
        />

        <SFCard sfElevation={2} containerClassName={styles.reportCard}>
          {isLoading && <Loader />}

          {!isLoading && (
            <div className={styles.reportCardContainer}>
              <ContactMap
                className={styles.leftMap}
                coords={mapCoords}
                address={
                  reportActive.general_information?.location?.address?.full
                }
                areas={areasName}
                contactSummary={contactSummaryValue}
                cadSummary={cadSummaryValue}
              />

              <div className={styles.reportCardContent}>
                <ReportCardHeader
                  className={styles.header}
                  incidentNumber={
                    report.general_information.incident_number as string
                  }
                  caseNumber={
                    report.general_information.state_related?.to_connect_reports
                      ?.event_identifier as string | undefined
                  }
                  isReportable={isStateReportable(reportActive)}
                  status={reportActive.status}
                  onScanQrCode={() => onScanQrCode(reportActive)}
                  onViewHistory={onViewHistory}
                  onSeeFullReport={onSeeFullReport}
                />

                <SFScrollable
                  className={styles.reportDataScrollable}
                  containerClassName={styles.reportData}
                >
                  <ContactMap
                    className={styles.topMap}
                    coords={mapCoords}
                    address={
                      reportActive.general_information?.location?.address?.full
                    }
                    areas={areasName}
                    contactSummary={contactSummaryValue}
                    cadSummary={cadSummaryValue}
                  />

                  <ReportCardInfo
                    className={styles.info}
                    report={reportActive}
                  />
                </SFScrollable>
              </div>

              {isLargeScreen && (
                <div className={styles.reportHistory}>
                  <ReportHistory
                    items={getHistoryItems(
                      report.versions as VersionValueType[]
                    )}
                    isAmendDisabled={isAmendDisabled}
                    isReportableDisabled={!showReportable}
                    selectedIndex={selectedItem}
                    tooltipType={report.tooltipType}
                    reportStatus={report.status}
                    onItemClick={onReportHistoryClick}
                    onAmend={onAmend}
                    onReportableStatusButtonClick={
                      onReportableStatusButtonClick
                    }
                  />
                </div>
              )}
            </div>
          )}
        </SFCard>
      </>
    );
  },
  (prevProps: ReportCardProps, nextProps: ReportCardProps) =>
    isEqualObject(prevProps.report, nextProps.report)
);
